import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {useState, useEffect} from 'react';

interface ConfirmDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  itemName: string;
  itemType: string;
  loading?: boolean;
  warningText?: string;
}

export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType,
  loading = false,
  warningText,
}: ConfirmDeleteModalProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  // Reset input when modal opens/closes
  useEffect(() => {
    if (open) {
      setInputValue('');
      setError('');
    }
  }, [open]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setError('');
  };

  const handleConfirm = () => {
    if (inputValue.trim() === itemName.trim()) {
      onConfirm();
    } else {
      setError(`Please enter the correct ${itemType} name`);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {warningText && (
          <Alert severity="warning" sx={{mb: 2}}>
            {warningText}
          </Alert>
        )}
        <DialogContentText sx={{mb: 2}}>
          To confirm the delete operation, please enter the name of the {itemType}:
          <Typography component="span" fontWeight="bold" color="error">
            {itemName}
          </Typography>
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label={`${itemType} name`}
          type="text"
          fullWidth
          variant="outlined"
          value={inputValue}
          onChange={handleInputChange}
          error={!!error}
          helperText={error}
          disabled={loading}
          onKeyPress={e => {
            if (e.key === 'Enter' && !loading) {
              handleConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={loading || inputValue.trim() !== itemName.trim()}
        >
          {loading ? 'Deleting...' : 'Confirm Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
